import React, { FC, useState, type PropsWithChildren } from "react";
import AccordionSection from "./AccordionSection";
import { Switch, Button } from "../../../../../components";
import type { ProfileLinkConfig } from "../../../../../client";
import MagicAccordionSection from "./MagicAccordionSection";

// These components are moved here to avoid circular dependencies with LinkCreatorSideMenu.tsx
// For a larger refactor, they might be placed in a shared components directory.
const SettingRow: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="flex items-center justify-between gap-2">{children}</div>
  );
};

const SettingText: FC<PropsWithChildren> = ({ children }) => {
  return <p className="text-gray-9 text-sm font-medium grow">{children}</p>;
};

interface ProfileSummaryLinkSettingsProps {
  disabled?: boolean;
  linkConfig?: ProfileLinkConfig;
  onUpdate: (payload: Partial<ProfileLinkConfig>) => void;
  onGenerateNew: () => void;
  /** Hide the box-color toggle — the template owns the summary surface. */
  hideBoxColor?: boolean;
  /** Hide the AI "Generate new" action (needs lead context not always present). */
  hideGenerate?: boolean;
}

const ProfileSummaryLinkSettings: FC<ProfileSummaryLinkSettingsProps> = ({
  disabled,
  linkConfig,
  onUpdate,
  onGenerateNew,
  hideBoxColor,
  hideGenerate,
}) => {
  const [isProfileSummarySectionExpanded, setIsProfileSummarySectionExpanded] =
    useState(false);

  return (
    <MagicAccordionSection
      title="Profile summary"
      className="p-2 rounded-2xl border-1 bg-[linear-gradient(90deg,rgba(255,40,166,0.05)_10%,rgba(57,220,234,0.05)_88%)] cursor-pointer"
      isMagic={true}
      subtitle={(() => {
        const activeSettings: string[] = [];
        if (!hideBoxColor) {
          if (linkConfig?.profileSummaryConfig?.boxColor === "#fff") {
            activeSettings.push("Box color");
          } else if (!linkConfig?.profileSummaryConfig?.boxColor) {
            activeSettings.push("No box color");
          }
        }
        if (linkConfig?.profileSummaryConfig?.showIntroText) {
          activeSettings.push("intro");
        } else if (!linkConfig?.profileSummaryConfig?.showIntroText) {
          activeSettings.push("no intro");
        }
        if (linkConfig?.profileSummaryConfig?.showHighlights) {
          activeSettings.push("experience bullets");
        } else if (!linkConfig?.profileSummaryConfig?.showHighlights) {
          activeSettings.push("no experience bullets");
        }
        if (linkConfig?.profileSummaryConfig?.showTopSkills) {
          activeSettings.push("top skills");
        } else if (!linkConfig?.profileSummaryConfig?.showTopSkills) {
          activeSettings.push("no top skills");
        }
        if (activeSettings.length === 0) {
          return "No settings active";
        }
        return activeSettings.join(", ");
      })()}
      expanded={isProfileSummarySectionExpanded}
      onToggle={() =>
        setIsProfileSummarySectionExpanded(!isProfileSummarySectionExpanded)
      }
    >
      <div className="flex flex-col gap-1 mt-2">
        {!hideBoxColor && (
          <SettingRow>
            <Switch
              checked={linkConfig?.profileSummaryConfig?.boxColor === "#fff"}
              disabled={disabled}
              onCheckedChange={(checked) => {
                if (typeof checked !== "boolean") return;

                onUpdate({
                  profileSummaryConfig: {
                    ...linkConfig?.profileSummaryConfig!,
                    boxColor: checked ? "#fff" : "",
                  },
                });
              }}
            />
            <SettingText>Box color</SettingText>
          </SettingRow>
        )}
        <SettingRow>
          <Switch
            checked={linkConfig?.profileSummaryConfig?.showIntroText}
            disabled={disabled}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              onUpdate({
                profileSummaryConfig: {
                  ...linkConfig?.profileSummaryConfig!,
                  showIntroText: checked,
                },
              });
            }}
          />
          <SettingText>Intro text</SettingText>
        </SettingRow>
        <SettingRow>
          <Switch
            checked={linkConfig?.profileSummaryConfig?.showHighlights ?? false}
            disabled={disabled}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              onUpdate({
                profileSummaryConfig: {
                  ...linkConfig?.profileSummaryConfig!,
                  showHighlights: checked,
                },
              });
            }}
          />
          <SettingText>Experience bullets</SettingText>
        </SettingRow>
        <SettingRow>
          <Switch
            checked={linkConfig?.profileSummaryConfig?.showTopSkills ?? false}
            disabled={disabled}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              onUpdate({
                profileSummaryConfig: {
                  ...linkConfig?.profileSummaryConfig!,
                  showTopSkills: checked,
                },
              });
            }}
          />
          <SettingText>Top skills</SettingText>
        </SettingRow>
        {!hideGenerate && (
          <SettingRow>
            <div className="flex items-center justify-center w-full">
              <Button
                className="mt-2"
                variant="magic-reversed"
                onClick={onGenerateNew}
                disabled={!linkConfig?.profileSummaryConfig?.showProfileSummary}
              >
                Generate new
              </Button>
            </div>
          </SettingRow>
        )}
      </div>
    </MagicAccordionSection>
  );
};

export default ProfileSummaryLinkSettings;
